// Redaction-aware cost display (Module 01 §2.16, §7). Field-level cost redaction
// is enforced SERVER-SIDE: a role without `catalogue.cost.read` receives a
// response with the money fields (`*_minor`, `breakdown`, NRV) omitted entirely.
// These components therefore treat PRESENCE as the signal and NEVER assume a
// cost field exists:
//
//   value === undefined  → the field was redacted → render nothing (no column,
//                          no cell) so a sales/floor role never sees a broken
//                          cost cell or an empty "Cost" column.
//   value === null       → present but not yet costed → render an em dash.
//   value is a number    → format and render.
//
// `costFieldPresent` lets a table hide the whole cost column when the field is
// absent on every row (i.e. the caller lacks cost.read).

import { useTranslation } from "react-i18next";

/**
 * Format integer minor units to a localized currency string. Pure + exported
 * for unit tests. The minor→major divisor follows the currency's own exponent
 * (KZT/USD = 2, JPY = 0) so per-meter integer costs never lose precision.
 */
export function formatMinor(minor: number, currency: string, locale?: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    const major = minor / Math.pow(10, digits);
    return fmt.format(major);
  } catch {
    // Unknown currency code: fall back to a plain minor-unit rendering rather
    // than throwing inside a render.
    return `${minor} ${currency}`;
  }
}

/** True when `key` is present (not redacted) on at least one row. */
export function costFieldPresent<T extends object>(rows: readonly T[], key: keyof T): boolean {
  return rows.some((row) => row[key] !== undefined);
}

type CostMinor = number | null | undefined;

export function CostValue({
  minor,
  currency,
  priceUnitQty,
  unitLabel,
}: {
  minor: CostMinor;
  currency?: string | undefined;
  priceUnitQty?: number | undefined;
  unitLabel?: string | undefined;
}) {
  const { t } = useTranslation();
  // Redacted → render nothing at all.
  if (minor === undefined) return null;
  // Present but not costed yet.
  if (minor === null || currency === undefined) {
    return <span className="cost cost-empty">{t("catalogue.cost.none")}</span>;
  }
  const basis =
    priceUnitQty !== undefined && priceUnitQty !== 1
      ? ` ${t("catalogue.cost.perUnit", { qty: unitLabel !== undefined ? `${priceUnitQty} ${unitLabel}` : priceUnitQty })}`
      : "";
  return (
    <span className="cost">
      {formatMinor(minor, currency)}
      {basis.length > 0 ? <span className="cost-basis">{basis}</span> : null}
    </span>
  );
}

export function NrvValue({ minor, currency }: { minor: CostMinor; currency?: string | undefined }) {
  const { t } = useTranslation();
  if (minor === undefined) return null;
  if (minor === null || currency === undefined) {
    return <span className="cost cost-empty">{t("catalogue.cost.none")}</span>;
  }
  return <span className="cost cost-nrv">{formatMinor(minor, currency)}</span>;
}

// ---- Breakdown tree ---------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function num(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}
function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * Explainable cost tree (§2.16 `breakdown`). Rendered only when `breakdown` is
 * present (i.e. not redacted). Defensive against shape drift — it reads the
 * documented `{lines, steps, credits, apportionment}` structure and skips what
 * it does not recognize rather than throwing. The S7 builder can enrich this;
 * the redaction guard and the section scaffold are the contract here.
 */
export function CostBreakdownTree({
  breakdown,
  currency,
}: {
  breakdown?: Record<string, unknown> | undefined;
  currency: string;
}) {
  const { t } = useTranslation();
  if (breakdown === undefined) return null;

  const lines = asArray(breakdown["lines"]);
  const steps = asArray(breakdown["steps"]);
  const credits = asArray(breakdown["credits"]);
  const apportionment = asRecord(breakdown["apportionment"]);

  return (
    <div className="cost-breakdown">
      {lines.length > 0 ? (
        <section className="cost-breakdown-section">
          <h4>{t("catalogue.cost.lines")}</h4>
          <ul>
            {lines.map((raw, i) => {
              const line = asRecord(raw) ?? {};
              const sku = str(line["component_sku"]) ?? str(line["bom_line_id"]) ?? "?";
              const cost = num(line["cost_minor"]);
              return (
                <li key={str(line["bom_line_id"]) ?? i}>
                  <span className="cost-breakdown-label">{sku}</span>
                  {cost !== undefined ? (
                    <span className="cost-breakdown-value">{formatMinor(cost, currency)}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {steps.length > 0 ? (
        <section className="cost-breakdown-section">
          <h4>{t("catalogue.cost.steps")}</h4>
          <ul>
            {steps.map((raw, i) => {
              const step = asRecord(raw) ?? {};
              const no = num(step["step_no"]);
              const cost = num(step["cost_minor"]);
              return (
                <li key={i}>
                  <span className="cost-breakdown-label">{no !== undefined ? `#${no}` : "?"}</span>
                  {cost !== undefined ? (
                    <span className="cost-breakdown-value">{formatMinor(cost, currency)}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {credits.length > 0 ? (
        <section className="cost-breakdown-section">
          <h4>{t("catalogue.cost.credits")}</h4>
          <ul>
            {credits.map((raw, i) => {
              const credit = asRecord(raw) ?? {};
              const sku = str(credit["product_sku"]) ?? str(credit["product_id"]) ?? "?";
              const cost = num(credit["credit_minor"]) ?? num(credit["cost_minor"]);
              return (
                <li key={i}>
                  <span className="cost-breakdown-label">{sku}</span>
                  {cost !== undefined ? (
                    <span className="cost-breakdown-value">−{formatMinor(cost, currency)}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {apportionment !== undefined ? (
        <section className="cost-breakdown-section">
          <h4>{t("catalogue.cost.apportionment")}</h4>
          <pre className="cost-breakdown-raw">{JSON.stringify(apportionment, null, 2)}</pre>
        </section>
      ) : null}
    </div>
  );
}
